import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { API_URL } from "../../../environment";
import { podeVerDiscadora } from "./access";
import { useFuncionalidades } from "../funcionalidades";

// Área de TESTE da integração CRM → discadora (03/11/2026). Só a conta BotConversa (9175).
// Fluxo: card entra numa etapa ligada a um mailing → o serviço discadora-crm (vigia a cada 5s)
// inscreve o contato → a discadora liga (quando o mailing está rodando) → resultados voltam aqui.

type Dict = Record<string, any>;

interface Estado {
  credencial: { configurada: boolean; email?: string | null; salva_em?: string | null };
  conexao?: { ok: boolean; quando: string; erro?: string | null; conta?: Dict | null } | null;
  trabalhadores: Record<string, { quando: string; erro?: string | null }>;
  inscricoes: Record<string, number>;
  resultados: number;
  fones_autorizados: string[];
  base_url: string;
  // envio de leads e resultados: pela chave de integração (X-CRM-Key), pelo login do painel ou "sem_chave"
  integracao?: { modo: "chave" | "painel" | "sem_chave"; chave_configurada: boolean };
  // a conta do CRM → cliente da discadora (04/11: 9175 = teste legado no painel; 151 = Goodata, só chave)
  conta?: { grupo: number; cliente_id?: string | null; modo: "chave" | "painel"; chave_configurada: boolean; integracao: string };
  atendentes_vinculados?: number;
  // 05/11: botão Ligar liberado para qualquer contato (desligado = só a lista de teste)
  ligar_todos?: boolean;
}

interface Etapa {
  id_funil_pai: number;
  nome_funil_pai: string;
  id_etapa: number;
  nome_etapa: string;
  cards: number;
  mailing_id?: number | null;
}

interface Mapa {
  id: number;
  nome_funil_pai: string;
  id_etapa: number;
  nome_etapa: string;
  mailing_id: number;
  mailing_nome?: string;
  ja_estavam: number;
  na_etapa_agora: number;
  inscricoes: Record<string, number>;
  campos?: EscolhaCampo[];
  campos_do_padrao?: boolean;
}

// campo do card escolhido pelo ID, com o nome de destino na discadora (renomear no CRM não quebra)
interface EscolhaCampo {
  id: number;
  chave: string;
}

interface UsuarioAtendente {
  id_user: number;
  nome?: string | null;
  atendente_id?: number | null;
}

interface Ligacao {
  id: number;
  card_id?: number;
  nome?: string;
  fone?: string;
  id_user?: number;
  atendente_id?: number;
  estado: string;
  erro?: string | null;
  criado_em: string;
  final?: boolean;
}

// Campos do card do CRM que vão junto no lead (GUIA-DEV-INTEGRACAO-CRM.md §4; pedido do Eduardo, 04/11)
interface CampoCard {
  id: number;
  label: string;
  tipo?: string;
  grupo?: string;
  global?: boolean;
  time?: string | null;
  sugestao?: string;
}

interface CamposEtapa {
  padrao?: boolean;
  usa_padrao?: boolean;
  id_etapa: number | null;
  nome_etapa: string;
  disponiveis: CampoCard[];
  escolhidos: EscolhaCampo[];
  reservadas?: string[];
  maximo: number;
  amostra?: { card_id: number; nome?: string | null; valores: Record<string, string> } | null;
}

interface Inscricao {
  id: number;
  card_id: number;
  fone: string;
  nome: string;
  nome_etapa: string;
  mailing_id: number;
  referencia: string;
  estado: string;
  erro?: string | null;
  lead_id?: number | null;
  duplicado: number;
  criado_em: string;
  telefone_e164?: string;
  ultimo_resultado?: string | null;
  ultima_tentativa?: number | null;
  tentativas_discadora: number;
  teve_pna: number;
  whats_estado?: string | null;
  whats_em?: string | null;
  whats_enviados?: number;
}

interface RegraWhats {
  id_etapa: number;
  ativo: boolean;
  gatilho: "pna" | "primeira" | "cada" | "esgotou";
  resultados: string[];
  mensagem: string;
  atualizado_em?: string;
  atualizado_por?: string;
}

interface Resultado {
  id: number;
  mailing_id: number;
  lead_id: number;
  resultado: string;
  tentativa_numero: number;
  primeira_nao_atendida: number;
  encerrada_em?: string;
  card_id?: number;
  nome?: string;
  fone?: string;
  telefone?: string;
  nome_etapa?: string;
}

interface Evento {
  id: number;
  quando: string;
  nivel: string;
  tipo: string;
  msg: string;
}

const ESTADOS: Record<string, { rotulo: string; cor: string; dica: string }> = {
  pendente: { rotulo: "Na fila", cor: "#d97706", dica: "Aguardando envio para a discadora" },
  enviando: { rotulo: "Enviando", cor: "#2563eb", dica: "Envio em andamento" },
  enviado: { rotulo: "Na discadora", cor: "#16a34a", dica: "Inscrito no mailing (a discadora liga quando o mailing estiver rodando)" },
  repetido: { rotulo: "Já estava no mailing", cor: "#6b7280", dica: "O mesmo telefone não entra duas vezes no mesmo mailing" },
  bloqueado: { rotulo: "Fora da lista de teste", cor: "#6b7280", dica: "Só os telefones de teste são inscritos" },
  conflito: { rotulo: "Conflito", cor: "#dc2626", dica: "A discadora já tem esse telefone/referência com outros dados" },
  erro: { rotulo: "Erro", cor: "#dc2626", dica: "A discadora recusou ou não respondeu" },
};

const RESULTADOS: Record<string, { rotulo: string; cor: string }> = {
  atendida: { rotulo: "Atendida", cor: "#16a34a" },
  nao_atendida: { rotulo: "Não atendida", cor: "#d97706" },
  ocupado: { rotulo: "Ocupado", cor: "#d97706" },
  falha: { rotulo: "Falha", cor: "#dc2626" },
  cancelada: { rotulo: "Cancelada", cor: "#6b7280" },
  agente_desistiu: { rotulo: "Atendente desistiu", cor: "#6b7280" },
};

// WhatsApp quando não conseguir contato (pedido do Eduardo, 04/11): quando e o quê conta como sem contato.
const GATILHOS_WHATS: { v: RegraWhats["gatilho"]; titulo: string; dica: string }[] = [
  {
    v: "pna",
    titulo: "Na 1ª tentativa discada e não atendida",
    dica: "Padrão definido pelo Eduardo: usa o sinal da discadora. Falha do ramal antes de discar não conta.",
  },
  { v: "primeira", titulo: "Na 1ª tentativa sem contato", dica: "Uma mensagem só, logo depois da primeira ligação que não deu certo." },
  { v: "cada", titulo: "A cada tentativa sem contato", dica: "Uma mensagem por ligação sem sucesso." },
  { v: "esgotou", titulo: "Quando esgotar as tentativas", dica: "Uma mensagem só, depois da última tentativa do mailing." },
];
const ROTULO_LIGACAO: Record<string, { rotulo: string; cor: string }> = {
  pedindo: { rotulo: "Pedindo o link", cor: "#6b7280" },
  aguardando_clique: { rotulo: "Aguardando abrir", cor: "#d97706" },
  preparado: { rotulo: "Preparado", cor: "#2563eb" },
  discando: { rotulo: "Discando", cor: "#2563eb" },
  aguardando_resultado: { rotulo: "Aguardando resultado", cor: "#2563eb" },
  finalizado: { rotulo: "Finalizada", cor: "#16a34a" },
  expirado: { rotulo: "Link expirou", cor: "#6b7280" },
  conflito: { rotulo: "Já pedida (409)", cor: "#dc2626" },
  recusado: { rotulo: "Recusada", cor: "#dc2626" },
  erro: { rotulo: "Erro", cor: "#dc2626" },
  erro_incerto: { rotulo: "Sem resposta (conferir)", cor: "#dc2626" },
};

const SEM_CONTATO: { v: string; rotulo: string }[] = [
  { v: "nao_atendida", rotulo: "Não atendida (inclui caixa postal)" },
  { v: "ocupado", rotulo: "Ocupado" },
  { v: "falha", rotulo: "Falha na ligação" },
  { v: "cancelada", rotulo: "Cancelada" },
  { v: "agente_desistiu", rotulo: "Atendente desistiu" },
];
const VARIAVEIS_WHATS = ["{primeiro_nome}", "{nome}", "{tentativa}", "{etapa}"];
const renderWhats = (modelo: string, vals: Record<string, string>): string =>
  (modelo || "")
    .replace(/\{(\w+)\}/g, (m, k) => (k in vals ? vals[k] : m))
    .replace(/,\s*([!?.])/g, "$1")
    .replace(/[ \t]+([,!?.])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
const WHATS_ESTADO: Record<string, { rotulo: string; cor: string }> = {
  enviado: { rotulo: "WhatsApp enviado", cor: "#16a34a" },
  pendente: { rotulo: "WhatsApp na fila", cor: "#d97706" },
  enviando: { rotulo: "WhatsApp saindo", cor: "#2563eb" },
  erro: { rotulo: "WhatsApp com erro", cor: "#dc2626" },
  bloqueado: { rotulo: "WhatsApp bloqueado (fora da lista de teste)", cor: "#6b7280" },
  ignorado: { rotulo: "WhatsApp não enviado (resultado antigo)", cor: "#6b7280" },
};

const NIVEL_COR: Record<string, string> = { ok: "#16a34a", info: "#2563eb", aviso: "#d97706", erro: "#dc2626" };

const formatFone = (f?: string | null): string => {
  let d = (f || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
  // celular guardado sem o 9º dígito (Uazapi): mostra como a discadora vai discar
  if (d.length === 10 && /[6-9]/.test(d[2])) d = `${d.slice(0, 2)}9${d.slice(2)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return f || "—";
};

const formatData = (iso?: string | null, comSegundos = false): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", ...(comSegundos ? { second: "2-digit" } : {}),
  });
};

const haQuanto = (iso?: string | null): string => {
  if (!iso) return "nunca";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `há ${s}s`;
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  return formatData(iso);
};

// Rótulo de um item da discadora (mailing, atendente, número) sem depender do formato exato.
const rotulo = (o: Dict, campos: string[]): string => {
  for (const c of campos) if (o && o[c] !== undefined && o[c] !== null && String(o[c]).trim()) return String(o[c]);
  return `#${o?.id ?? "?"}`;
};

// ── conversas (gravação, transcrição, extrações) ─────────────────────────────
// O formato exato da API de conversas ainda não foi visto com dados reais (guia do Postman de 03/11):
// tudo aqui procura os campos por nomes prováveis e cai num "ver JSON" quando não reconhece.
const primeiro = (o: Dict | null | undefined, chaves: string[]): any => {
  if (!o) return undefined;
  for (const k of chaves) if (o[k] !== undefined && o[k] !== null && String(o[k]).trim() !== "") return o[k];
  return undefined;
};

const CONV_DATA = ["inicio", "iniciada_em", "inicio_em", "criado_em", "created_at", "data", "quando", "encerrada_em"];
const CONV_FONE = ["telefone_e164", "telefone", "numero", "destino", "para"];
const CONV_DURACAO = ["duracao_s", "duracao", "duracao_seg", "segundos", "duration"];
const CONV_STATUS = ["status", "situacao", "estado", "processamento"];

const formatDuracao = (v: any): string => {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return v ? String(v) : "—";
  const t = Math.round(n);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

// Campos técnicos da conversa que não ajudam na tela (continuam no "JSON completo").
const CONV_OCULTOS = new Set(["audio_sha256", "configuracao", "cliente_id", "prompt_assinatura", "transcrita"]);

const ehDataIso = (v: any) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);

const ROTULO_CAMPO: Record<string, string> = {
  id: "Conversa", referencia: "Referência (tentativa/chamada)", mailing_id: "Mailing", lead_id: "Lead",
  tentativa_id: "Tentativa", telefone_e164: "Telefone", telefone: "Telefone", inicio: "Início", criado_em: "Criada",
  encerrada_em: "Encerrada", duracao_s: "Duração", duracao: "Duração", status: "Situação", tipo: "Tipo",
  classificacao: "Classificação", atendente_id: "Atendente", origem: "Origem", tarefas: "Tarefas", extracoes: "Extrações",
  analises: "Análises", resumo: "Resumo", interesse_objecoes: "Interesse e objeções", interesse: "Interesse",
  objecoes: "Objeções", proximos_passos: "Próximos passos", proximo_passo: "Próximo passo", data_retorno: "Data de retorno",
  transcricao: "Transcrição", modelo_transcricao: "Modelo da transcrição", modelo_extracao: "Modelo das análises",
  audio_bytes: "Tamanho do áudio",
};

// "proximos_passos" → "Próximos passos" (conhecidos), senão "algum campo" → "Algum campo"
const humano = (k: any): string => {
  const s = String(k ?? "");
  if (ROTULO_CAMPO[s]) return ROTULO_CAMPO[s];
  const t = s.replace(/[_-]+/g, " ").trim();
  return t ? t[0].toUpperCase() + t.slice(1) : s;
};

// Resultado de análise: texto, lista simples ou objeto raso viram linhas legíveis; o resto, JSON.
// Formato real (04/11): {"resumo": "..."}, {"objecoes": [], "interesse": null}, {"data_retorno": null, ...}.
const textoDoResultado = (v: any): string => {
  if (v === null || v === undefined) return "";
  if (typeof v !== "object") return String(v);
  const simples = (x: any) => x === null || typeof x !== "object" || (Array.isArray(x) && x.every((y) => y === null || typeof y !== "object"));
  const fmt = (x: any) =>
    Array.isArray(x) ? (x.length ? x.join(", ") : "nenhuma") : x === null || x === "" ? "não identificado" : ehDataIso(x) ? formatData(x) : String(x);
  if (Array.isArray(v)) return v.every((y) => typeof y !== "object" || y === null) ? (v.length ? v.map((y) => `• ${y}`).join("\n") : "nenhuma") : JSON.stringify(v, null, 2);
  const entradas = Object.entries(v);
  if (entradas.length === 1 && simples(entradas[0][1])) return fmt(entradas[0][1]); // {"resumo": "..."} → só o texto
  if (entradas.every(([, x]) => simples(x))) return entradas.map(([k, x]) => `${humano(k)}: ${fmt(x)}`).join("\n");
  return JSON.stringify(v, null, 2);
};

// Situação de processamento/tarefa → cor e texto curto.
const situacaoTarefa = (s: any): { cor: string; texto: string; dica?: string } => {
  const t = String(s ?? "").toLowerCase();
  if (!t) return { cor: "#6b7280", texto: "—" };
  if (/conclu|pront|ok|feit|done|complet|sucesso/.test(t)) return { cor: "#16a34a", texto: String(s) };
  if (/incert/.test(t)) return { cor: "#d97706", texto: String(s), dica: "Precisa de conferência humana no painel; não é refeita sozinha." };
  if (/pend|process|fila|aguard|andamento|run/.test(t))
    return { cor: "#2563eb", texto: String(s), dica: "Processamento assíncrono: pendente não é falha. Atualize daqui a pouco." };
  if (/falh|erro|fail/.test(t)) return { cor: "#dc2626", texto: String(s) };
  return { cor: "#6b7280", texto: String(s) };
};

// Transcrição: texto corrido ou lista de falas ({falante/quem/speaker, texto/text}).
const falasDa = (v: any): { quem?: string; texto: string }[] | string | null => {
  if (v === undefined || v === null || v === "") return null;
  if (typeof v === "string") return v;
  if (Array.isArray(v))
    return v.map((x) =>
      typeof x === "string"
        ? { texto: x }
        : { quem: primeiro(x, ["falante", "quem", "speaker", "papel", "canal", "role"]), texto: String(primeiro(x, ["texto", "text", "fala", "conteudo"]) ?? JSON.stringify(x)) }
    );
  if (typeof v === "object") return falasDa(primeiro(v, ["texto", "text", "segmentos", "falas", "conteudo"]) ?? JSON.stringify(v, null, 2));
  return String(v);
};

const Discadora = () => {
  const navigate = useNavigate();
  // 05/11: acesso pela tabela funcionalidade_conta (discadora / discadora_ligar), lida a cada página.
  const { funcionalidades, carregado: funcCarregadas } = useFuncionalidades();
  const autorizado = podeVerDiscadora(funcionalidades);
  const isDark = useSelector((state: any) => state.themeSetting?.dataTheme) === "dark";
  const idGrupo = localStorage.getItem("id_grupo") || "";
  const usuario = localStorage.getItem("user_email") || localStorage.getItem("id_user") || "";

  const [estado, setEstado] = useState<Estado | null>(null);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [mapas, setMapas] = useState<Mapa[]>([]);
  const [inscricoes, setInscricoes] = useState<Inscricao[]>([]);
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [recursos, setRecursos] = useState<{ mailings: Dict[]; atendentes: Dict[]; numeros: Dict[] } | null>(null);
  const [erroRecursos, setErroRecursos] = useState<string | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  // formulários
  const [trocandoCred, setTrocandoCred] = useState(false);
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [mailingSel, setMailingSel] = useState<string>("");
  const [novoAberto, setNovoAberto] = useState(false);
  const [novo, setNovo] = useState({
    nome: `CRM — teste de integração ${new Date().toLocaleDateString("pt-BR")}`,
    atendente_id: "",
    numero_id: "",
    // Política sugerida no guia do Postman (03/11): até 20 tentativas por pessoa, 1 min entre elas, 08–18h.
    tentativas_max: 20,
    intervalo: 1,
    horario_ini: "08:00",
    horario_fim: "18:00",
  });
  const [funilSel, setFunilSel] = useState<string>("");
  const [etapaSel, setEtapaSel] = useState<string>("");
  const [mapaMailing, setMapaMailing] = useState<string>("");
  const [foneNovo, setFoneNovo] = useState("");
  const [confirmarInicio, setConfirmarInicio] = useState<Dict | null>(null);
  const [regras, setRegras] = useState<Record<number, RegraWhats>>({});
  const [regraPadrao, setRegraPadrao] = useState<Omit<RegraWhats, "id_etapa" | "ativo"> | null>(null);
  const [editandoRegra, setEditandoRegra] = useState<(RegraWhats & { nome_etapa: string }) | null>(null);
  const [editandoCampos, setEditandoCampos] = useState<{
    id_etapa: number | null; // null = padrão da conta
    nome_etapa: string;
    carregando: boolean;
    erro: string | null;
    dados: CamposEtapa | null;
    escolhidos: EscolhaCampo[];
  } | null>(null);
  const [atendentesCrm, setAtendentesCrm] = useState<UsuarioAtendente[] | null>(null);
  const [atendEdit, setAtendEdit] = useState<Record<number, string>>({});
  const [ligacoes, setLigacoes] = useState<Ligacao[]>([]);
  const [leadsMailing, setLeadsMailing] = useState<Dict[] | null>(null);
  const [registroAberto, setRegistroAberto] = useState(true);
  const [conversas, setConversas] = useState<Dict[] | null>(null);
  const [erroConversas, setErroConversas] = useState<string | null>(null);
  const [carregandoConversas, setCarregandoConversas] = useState(false);
  const [conversaAberta, setConversaAberta] = useState<{ id: number; dados: Dict | null; erro: string | null; carregando: boolean } | null>(null);
  const [verJson, setVerJson] = useState(false);
  const carregandoRef = useRef(false);

  useEffect(() => {
    // só expulsa depois de ler as funções desta página (a cópia guardada pode estar velha ou vazia)
    if (funcCarregadas && !autorizado) navigate(all_routes.chat, { replace: true });
  }, [autorizado, funcCarregadas, navigate]);

  // ── API ────────────────────────────────────────────────────────────────
  const api = useCallback(
    async (metodo: "GET" | "POST" | "DELETE", caminho: string, corpo?: Dict) => {
      const q = new URLSearchParams({ id_grupo: idGrupo, usuario });
      const resp = await fetch(`${API_URL}/discadora${caminho}${caminho.includes("?") ? "&" : "?"}${q.toString()}`, {
        method: metodo,
        headers: corpo ? { "Content-Type": "application/json" } : undefined,
        body: corpo ? JSON.stringify({ ...corpo, id_grupo: Number(idGrupo), usuario }) : undefined,
      });
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(dados?.error || `Falha (HTTP ${resp.status})`);
      return dados;
    },
    [idGrupo, usuario]
  );

  const carregarPainel = useCallback(async () => {
    if (carregandoRef.current) return;
    carregandoRef.current = true;
    try {
      const [e, m, i, r, ev, rg, lg] = await Promise.all([
        api("GET", "/estado"),
        api("GET", "/mapa"),
        api("GET", "/inscricoes?limite=100"),
        api("GET", "/resultados?limite=100"),
        api("GET", "/eventos?limite=60"),
        api("GET", "/regras"),
        api("GET", "/ligacoes?limite=15"),
      ]);
      setLigacoes(Array.isArray(lg) ? lg : []);
      setEstado(e);
      setMapas(m);
      setInscricoes(i);
      setResultados(r);
      setEventos(ev);
      setRegras(Object.fromEntries((rg?.regras || []).map((x: RegraWhats) => [x.id_etapa, x])));
      if (rg?.padrao) setRegraPadrao(rg.padrao);
      setErroGeral(null);
    } catch (err: any) {
      setErroGeral(err?.message || "Não consegui falar com o serviço da discadora.");
    } finally {
      carregandoRef.current = false;
    }
  }, [api]);

  const carregarRecursos = useCallback(async () => {
    try {
      const r = await api("GET", "/recursos");
      setRecursos(r);
      setErroRecursos(null);
    } catch (err: any) {
      setErroRecursos(err?.message || "Não consegui ler os dados da discadora.");
    }
  }, [api]);

  const carregarAtendentes = useCallback(async () => {
    try {
      const r = await api("GET", "/atendentes-crm");
      setAtendentesCrm(r?.usuarios || []);
    } catch {
      setAtendentesCrm([]);
    }
  }, [api]);

  const carregarEtapas = useCallback(async () => {
    try {
      setEtapas(await api("GET", "/etapas"));
    } catch (err: any) {
      setErroGeral(err?.message || "Não consegui ler as etapas do CRM.");
    }
  }, [api]);

  useEffect(() => {
    if (!autorizado) return;
    carregarPainel();
    carregarEtapas();
    carregarAtendentes();
    const t = setInterval(carregarPainel, 5000);
    return () => clearInterval(t);
  }, [autorizado, carregarPainel, carregarEtapas, carregarAtendentes]);

  const credOk = !!estado?.credencial?.configurada;
  // conta só com a chave de integração (151 Goodata): mailings, atendentes e conversas ficam com a gestão
  const contaChave = estado?.conta?.modo === "chave";
  useEffect(() => {
    if (credOk && !contaChave && estado && !recursos) carregarRecursos();
  }, [credOk, contaChave, estado, recursos, carregarRecursos]);

  // Conversas vêm direto da discadora (sem cópia aqui): carrega uma vez e no botão, sem ficar consultando.
  const carregarConversas = useCallback(async () => {
    setCarregandoConversas(true);
    try {
      const r = await api("GET", "/conversas?limite=50");
      setConversas(Array.isArray(r?.itens) ? r.itens : []);
      setErroConversas(null);
    } catch (err: any) {
      setErroConversas(err?.message || "Não consegui ler as conversas da discadora.");
    } finally {
      setCarregandoConversas(false);
    }
  }, [api]);
  useEffect(() => {
    if (credOk && !contaChave && estado && conversas === null && !erroConversas) carregarConversas();
  }, [credOk, contaChave, estado, conversas, erroConversas, carregarConversas]);

  const abrirConversa = useCallback(async (id: number) => {
    setVerJson(false);
    setConversaAberta((atual) => ({ id, dados: atual?.id === id ? atual.dados : null, erro: null, carregando: true }));
    try {
      const dados = await api("GET", `/conversas/${id}`);
      setConversaAberta((atual) => (atual?.id === id ? { id, dados, erro: null, carregando: false } : atual));
    } catch (err: any) {
      setConversaAberta((atual) => (atual?.id === id ? { id, dados: atual.dados, erro: err?.message || "Falhou.", carregando: false } : atual));
    }
  }, [api]);

  const abrirCampos = useCallback(async (id_etapa: number | null, nome_etapa: string) => {
    setEditandoCampos({ id_etapa, nome_etapa, carregando: true, erro: null, dados: null, escolhidos: [] });
    try {
      const dados: CamposEtapa = await api("GET", id_etapa ? `/campos?id_etapa=${id_etapa}` : "/campos?id_etapa=padrao");
      setEditandoCampos((a) => (a?.id_etapa === id_etapa ? { ...a, carregando: false, dados, escolhidos: dados.escolhidos || [] } : a));
    } catch (err: any) {
      setEditandoCampos((a) => (a?.id_etapa === id_etapa ? { ...a, carregando: false, erro: err?.message || "Falhou." } : a));
    }
  }, [api]);

  const urlAudio = (id: number) =>
    `${API_URL}/discadora/conversas/${id}/audio?${new URLSearchParams({ id_grupo: idGrupo, usuario }).toString()}`;

  const executar = async (chave: string, fn: () => Promise<any>, sucesso?: string) => {
    setOcupado(chave);
    setAviso(null);
    try {
      await fn();
      if (sucesso) setAviso({ tipo: "ok", texto: sucesso });
      await carregarPainel();
    } catch (err: any) {
      setAviso({ tipo: "erro", texto: err?.message || "Algo deu errado." });
    } finally {
      setOcupado(null);
    }
  };

  // ── derivados ──────────────────────────────────────────────────────────
  const mailings = useMemo(() => recursos?.mailings || [], [recursos]);
  // Com um só atendente/número ativo na conta, já vem escolhido (os IDs mudam de conta para conta).
  useEffect(() => {
    if (!recursos) return;
    const unico = (l: Dict[]) => {
      const ativos = (l || []).filter((x) => x?.ativo !== false);
      return ativos.length === 1 ? String(ativos[0].id) : "";
    };
    setNovo((n) => ({
      ...n,
      atendente_id: n.atendente_id || unico(recursos.atendentes),
      numero_id: n.numero_id || unico(recursos.numeros),
    }));
  }, [recursos]);
  const mailingAtual = useMemo(() => mailings.find((m) => String(m.id) === mailingSel) || null, [mailings, mailingSel]);
  useEffect(() => {
    if (!mailingSel && mailings.length) setMailingSel(String(mailings[mailings.length - 1].id));
  }, [mailings, mailingSel]);

  const funis = useMemo(() => {
    const m = new Map<string, string>();
    etapas.forEach((e) => m.set(String(e.id_funil_pai), e.nome_funil_pai));
    return Array.from(m.entries());
  }, [etapas]);
  useEffect(() => {
    if (!funilSel && funis.length) {
      const teste = funis.find(([, n]) => /discadora/i.test(n));
      setFunilSel((teste || funis[0])[0]);
    }
  }, [funis, funilSel]);
  const etapasDoFunil = useMemo(() => etapas.filter((e) => String(e.id_funil_pai) === funilSel), [etapas, funilSel]);
  // No funil de teste, a etapa "Ligar" já vem escolhida.
  useEffect(() => {
    if (etapaSel) return;
    const ligar = etapasDoFunil.find((e) => /^ligar$/i.test(e.nome_etapa.trim()));
    if (ligar) setEtapaSel(String(ligar.id_etapa));
  }, [etapasDoFunil, etapaSel]);

  const inscritosNoMailing = (mid: number) =>
    inscricoes.filter((i) => i.mailing_id === mid && i.estado === "enviado").length;

  if (!autorizado) return null;

  // ── visual ─────────────────────────────────────────────────────────────
  const bgCard = isDark ? "#1f2c34" : "#ffffff";
  const bgHead = isDark ? "#172229" : "#f8fafc";
  const border = isDark ? "#2a3942" : "#e6e9ef";
  const textMain = isDark ? "#e9edef" : "#1a1a2e";
  const textMuted = isDark ? "#8696a0" : "#6b7280";
  const card: React.CSSProperties = { background: bgCard, border: `1px solid ${border}`, borderRadius: 12, padding: 16 };
  const lbl: React.CSSProperties = { fontSize: 11, color: textMuted, fontWeight: 600, marginBottom: 2, display: "block" };
  const th: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: textMuted, textTransform: "uppercase", letterSpacing: 0.3, padding: "9px 10px",
    whiteSpace: "nowrap", background: bgHead, borderBottom: `1px solid ${border}`, position: "sticky", top: 0, zIndex: 1, textAlign: "left",
  };
  const td: React.CSSProperties = { fontSize: 13, color: textMain, padding: "9px 10px", borderBottom: `1px solid ${border}`, verticalAlign: "top" };

  const Badge = ({ cor, children, dica }: { cor: string; children: React.ReactNode; dica?: string }) => (
    <span
      title={dica}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, color: cor,
        background: `${cor}1a`, border: `1px solid ${cor}40`, borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );

  const Titulo = ({ n, icone, texto, dir }: { n?: number; icone: string; texto: string; dir?: React.ReactNode }) => (
    <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
      <div className="d-flex align-items-center gap-2" style={{ color: textMain, fontWeight: 700, fontSize: 15 }}>
        {n !== undefined && (
          <span style={{ width: 24, height: 24, borderRadius: 999, background: "#2563eb", color: "#fff", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {n}
          </span>
        )}
        <i className={`ti ti-${icone}`} style={{ color: "#2563eb" }} />
        {texto}
      </div>
      {dir}
    </div>
  );

  const vivo = (nome: string) => {
    const b = estado?.trabalhadores?.[nome];
    const atrasado = !b || Date.now() - new Date(b.quando).getTime() > 30000;
    const cor = !b ? "#6b7280" : b.erro && b.erro !== "sem credencial" ? "#dc2626" : atrasado ? "#d97706" : "#16a34a";
    const txt = !b ? "parado" : b.erro === "sem credencial" ? "aguardando credencial" : b.erro ? "com erro" : haQuanto(b.quando);
    return (
      <Badge cor={cor} dica={b?.erro || undefined}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: cor, display: "inline-block" }} />
        {nome}: {txt}
      </Badge>
    );
  };

  const conta = estado?.conexao?.conta || null;
  const contaResumo = conta
    ? Object.entries(conta)
        .filter(([, v]) => ["string", "number", "boolean"].includes(typeof v))
        .slice(0, 5)
    : [];

  const cont = estado?.inscricoes || {};

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ width: "100%" }}>
          {/* Cabeçalho */}
          <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h4 className="mb-1" style={{ color: textMain }}>
                <i className="ti ti-phone-call me-2" style={{ color: "#2563eb" }} />
                Discadora <span style={{ fontSize: 13, color: "#d97706", fontWeight: 700 }}>· área de teste</span>
              </h4>
              <p className="mb-0" style={{ color: textMuted, fontSize: 13, maxWidth: 760 }}>
                Quando um card entra numa etapa ligada a um mailing, o CRM inscreve o contato na discadora. Ela liga
                enquanto o mailing estiver <b>rodando</b>, e os resultados voltam para cá. Só esta conta vê esta área.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {vivo("vigia")}
              {vivo("envio")}
              {vivo("resultados")}
            </div>
          </div>

          {erroGeral && (
            <div className="mb-3 p-2" style={{ ...card, borderColor: "#dc2626", color: "#dc2626", fontSize: 13 }}>
              <i className="ti ti-alert-triangle me-1" />
              {erroGeral}
            </div>
          )}
          {aviso && (
            <div
              className="mb-3 p-2 d-flex justify-content-between align-items-start gap-2"
              style={{ ...card, borderColor: aviso.tipo === "ok" ? "#16a34a" : "#dc2626", color: aviso.tipo === "ok" ? "#16a34a" : "#dc2626", fontSize: 13 }}
            >
              <span>
                <i className={`ti ti-${aviso.tipo === "ok" ? "circle-check" : "alert-triangle"} me-1`} />
                {aviso.texto}
              </span>
              <button type="button" className="btn btn-sm p-0" style={{ color: textMuted }} onClick={() => setAviso(null)}>
                <i className="ti ti-x" />
              </button>
            </div>
          )}

          {/* Como testar */}
          <div className="mb-3" style={{ ...card, background: isDark ? "#1b2a33" : "#f0f6ff", borderColor: isDark ? "#2a3942" : "#cfe0ff" }}>
            <div style={{ fontWeight: 700, color: textMain, fontSize: 13.5, marginBottom: 6 }}>
              <i className="ti ti-list-check me-1" style={{ color: "#2563eb" }} />
              Roteiro do teste
            </div>
            {contaChave ? (
              <ol className="mb-0" style={{ color: textMain, fontSize: 13, paddingLeft: 20, lineHeight: 1.7, listStyle: "decimal" }}>
                <li>
                  A gestão da discadora gera a <b>chave de integração</b> desta conta (cliente {estado?.conta?.cliente_id}). Ela fica só no
                  servidor do CRM; o passo 1 mostra se já chegou.
                </li>
                <li>Ligue a etapa ao <b>ID do mailing</b> informado pela gestão (passo 3) e escolha os <b>campos do card</b> que vão junto.</li>
                <li>
                  Para ligar um contato por vez: ligue o seu usuário ao <b>atendente da discadora</b> (seção Botão Ligar) e use{" "}
                  <b>Ligar</b> no card do Kanban (Lista de Leads). O botão não inscreve ninguém no mailing.
                </li>
                <li>Iniciar e pausar o mailing é com a gestão. Os resultados voltam para cá (passo 5).</li>
              </ol>
            ) : (
            <ol className="mb-0" style={{ color: textMain, fontSize: 13, paddingLeft: 20, lineHeight: 1.7, listStyle: "decimal" }}>
              <li>Salve a credencial do painel da discadora (passo 1).</li>
              <li>
                Crie um mailing <b>novo</b>. Ele nasce <b>pausado</b> (passo 2). Os mailings já usados não recomeçam: quem já
                foi atendido não liga de novo.
              </li>
              <li>
                Ligue a etapa <b>"Ligar"</b> do funil <b>"Teste Discadora"</b> ao mailing (passo 3). Se quiser, configure ali o{" "}
                <b>WhatsApp quando não conseguir contato</b>.
              </li>
              <li>No painel da discadora: abra o fone, registre o ramal e ative <b>"Disponível para discagem automática"</b>.</li>
              <li>Clique em <b>Iniciar discagem</b> e arraste o card do seu número para "Ligar". Quando terminar, <b>pause</b>.</li>
              <li>A gravação, a transcrição e as análises da ligação aparecem em <b>Conversas</b> (passo 6). Elas podem levar alguns minutos.</li>
            </ol>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 16, marginBottom: 16 }}>
            {/* 1. Conexão */}
            <div style={card}>
              <Titulo
                n={1}
                icone="plug-connected"
                texto="Conexão"
                dir={
                  contaChave ? (
                    !estado?.conta?.chave_configurada ? (
                      <Badge cor="#d97706">aguardando a chave</Badge>
                    ) : estado?.conexao && !estado.conexao.ok ? (
                      <Badge cor="#dc2626" dica={estado.conexao.erro || ""}>chave recusada</Badge>
                    ) : (
                      <Badge cor="#16a34a">chave configurada</Badge>
                    )
                  ) : credOk && estado?.conexao ? (
                    estado.conexao.ok ? <Badge cor="#16a34a">conectada</Badge> : <Badge cor="#dc2626" dica={estado.conexao.erro || ""}>falhou</Badge>
                  ) : (
                    <Badge cor="#6b7280">sem credencial</Badge>
                  )
                }
              />
              {contaChave ? (
                <>
                  <div style={{ fontSize: 13, color: textMain }}>
                    <div><span style={lbl}>Cliente na discadora</span>#{estado?.conta?.cliente_id}</div>
                    <div className="mt-2">
                      <span style={lbl}>Autenticação</span>
                      Chave de integração (X-CRM-Key), guardada só no servidor do CRM.
                    </div>
                    <div className="mt-2" style={{ fontSize: 12.5, color: estado?.conta?.chave_configurada ? textMuted : "#d97706" }}>
                      {estado?.conta?.chave_configurada
                        ? "Envio de leads, resultados e o botão Ligar saem pela chave."
                        : "A chave ainda não foi configurada: nada é enviado à discadora até ela chegar."}
                    </div>
                    {estado?.conexao?.erro && !estado.conexao.ok && (
                      <div className="mt-2" style={{ color: "#dc2626", fontSize: 12.5 }}>{estado.conexao.erro}</div>
                    )}
                    <div className="mt-2" style={{ fontSize: 12, color: textMuted }}>
                      Última conferência: {haQuanto(estado?.conexao?.quando)} · {estado?.base_url}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!ocupado || !estado?.conta?.chave_configurada}
                      onClick={() => executar("testar", async () => { await api("POST", "/testar", {}); }, "Chave conferida: a discadora aceitou.")}>
                      <i className="ti ti-refresh me-1" />{ocupado === "testar" ? "Testando…" : "Testar a chave"}
                    </button>
                  </div>
                </>
              ) : credOk && !trocandoCred ? (
                <>
                  <div style={{ fontSize: 13, color: textMain }}>
                    <div><span style={lbl}>Login no painel</span>{estado?.credencial.email}</div>
                    {contaResumo.length > 0 && (
                      <div className="mt-2">
                        <span style={lbl}>Conta na discadora</span>
                        {contaResumo.map(([k, v]) => (
                          <div key={k} style={{ fontSize: 12.5 }}>
                            <span style={{ color: textMuted }}>{k}:</span> {String(v)}
                          </div>
                        ))}
                      </div>
                    )}
                    {estado?.conexao?.erro && !estado.conexao.ok && (
                      <div className="mt-2" style={{ color: "#dc2626", fontSize: 12.5 }}>{estado.conexao.erro}</div>
                    )}
                    <div className="mt-2" style={{ fontSize: 12, color: textMuted }}>
                      Última conferência: {haQuanto(estado?.conexao?.quando)} · {estado?.base_url}
                    </div>
                    <div className="mt-1" style={{ fontSize: 12, color: textMuted }}>
                      <i className={`ti ti-${estado?.integracao?.modo === "chave" ? "key" : "login"} me-1`} />
                      Envio de leads e resultados:{" "}
                      {estado?.integracao?.modo === "chave"
                        ? "pela chave de integração (X-CRM-Key)."
                        : "pelo login do painel (a chave de integração ainda não foi configurada)."}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!ocupado}
                      onClick={() => executar("testar", async () => { await api("POST", "/testar", {}); await carregarRecursos(); }, "Conexão conferida.")}>
                      <i className="ti ti-refresh me-1" />{ocupado === "testar" ? "Testando…" : "Testar conexão"}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setTrocandoCred(true)}>
                      Trocar credencial
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={!!ocupado}
                      onClick={() => executar("remover", async () => { await api("DELETE", "/credencial"); setRecursos(null); }, "Credencial removida.")}>
                      Remover
                    </button>
                  </div>
                </>
              ) : (
                <form
                  onSubmit={(ev) => {
                    ev.preventDefault();
                    executar("cred", async () => {
                      await api("POST", "/credencial", { email: email.trim(), senha });
                      setSenha("");
                      setTrocandoCred(false);
                      setRecursos(null);
                    }, "Credencial salva e login conferido.");
                  }}
                >
                  <label style={lbl}>E-mail da conta cliente do painel</label>
                  <input className="form-control form-control-sm mb-2" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  <label style={lbl}>Senha</label>
                  <input className="form-control form-control-sm mb-2" type="password" autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} required />
                  <div style={{ fontSize: 12, color: textMuted }} className="mb-2">
                    <i className="ti ti-lock me-1" />
                    A senha fica só no servidor do CRM e nunca volta para a tela. A senha do ramal não serve aqui.
                  </div>
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={!!ocupado}>
                      {ocupado === "cred" ? "Conferindo…" : "Salvar e testar"}
                    </button>
                    {credOk && (
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setTrocandoCred(false)}>
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>

            {/* 2. Mailing */}
            <div style={card}>
              <Titulo
                n={2}
                icone="list-numbers"
                texto="Mailing"
                dir={
                  credOk && !contaChave && (
                    <button type="button" className="btn btn-sm btn-link p-0" onClick={carregarRecursos} title="Recarregar da discadora">
                      <i className="ti ti-refresh" />
                    </button>
                  )
                }
              />
              {contaChave ? (
                <div style={{ fontSize: 13, color: textMain }}>
                  Com a chave de integração, os mailings são cadastrados, iniciados e pausados pela <b>gestão da discadora</b>.
                  Informe o ID que ela passar na etapa (passo 3).
                </div>
              ) : !credOk ? (
                <div style={{ fontSize: 13, color: textMuted }}>Salve a credencial primeiro.</div>
              ) : erroRecursos ? (
                <div style={{ fontSize: 13, color: "#dc2626" }}>{erroRecursos}</div>
              ) : !recursos ? (
                <div style={{ fontSize: 13, color: textMuted }}>Carregando da discadora…</div>
              ) : (
                <>
                  {mailings.length ? (
                    <>
                      <label style={lbl}>Mailing</label>
                      <select className="form-select form-select-sm mb-2" value={mailingSel} onChange={(e) => { setMailingSel(e.target.value); setLeadsMailing(null); }}>
                        {mailings.map((m) => (
                          <option key={m.id} value={m.id}>
                            #{m.id} {rotulo(m, ["nome"])} {m.status ? `— ${m.status}` : ""}
                          </option>
                        ))}
                      </select>
                      {mailingAtual && (
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <Badge cor={mailingAtual.status === "rodando" ? "#16a34a" : mailingAtual.status === "pausado" ? "#d97706" : "#6b7280"}>
                            {mailingAtual.status || "status ?"}
                          </Badge>
                          {mailingAtual.tentativas_max && (
                            <span style={{ fontSize: 12, color: textMuted }}>
                              {mailingAtual.tentativas_max} tentativa(s)
                              {mailingAtual.horario_ini ? ` · ${mailingAtual.horario_ini}–${mailingAtual.horario_fim}` : ""}
                            </span>
                          )}
                          <span style={{ fontSize: 12, color: textMuted }}>· {inscritosNoMailing(Number(mailingAtual.id))} inscrito(s) por aqui</span>
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2">
                        <button type="button" className="btn btn-sm btn-success" disabled={!mailingAtual || !!ocupado}
                          onClick={() => setConfirmarInicio(mailingAtual)}>
                          <i className="ti ti-player-play me-1" />Iniciar discagem
                        </button>
                        <button type="button" className="btn btn-sm btn-warning" disabled={!mailingAtual || !!ocupado}
                          onClick={() => executar("pausar", async () => { await api("POST", `/mailings/${mailingAtual!.id}/status`, { status: "pausado" }); await carregarRecursos(); }, `Mailing #${mailingAtual!.id} pausado.`)}>
                          <i className="ti ti-player-pause me-1" />Pausar
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!mailingAtual}
                          onClick={async () => {
                            try { setLeadsMailing(await api("GET", `/mailings/${mailingAtual!.id}/leads`)); }
                            catch (err: any) { setAviso({ tipo: "erro", texto: err?.message }); }
                          }}>
                          Ver fila
                        </button>
                      </div>
                      {leadsMailing && (
                        <div className="mt-2" style={{ fontSize: 12.5, color: textMain, maxHeight: 140, overflow: "auto", border: `1px solid ${border}`, borderRadius: 8, padding: 8 }}>
                          {!leadsMailing.length ? (
                            <span style={{ color: textMuted }}>Nenhum lead neste mailing.</span>
                          ) : (
                            leadsMailing.map((l) => (
                              <div key={l.id}>
                                #{l.id} {rotulo(l, ["nome"])} · {formatFone(l.telefone_e164)} {l.status ? `· ${l.status}` : ""}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: textMuted }} className="mb-2">Nenhum mailing nesta conta ainda.</div>
                  )}

                  <button type="button" className="btn btn-sm btn-link px-0 mt-2" onClick={() => setNovoAberto(!novoAberto)}>
                    <i className={`ti ti-chevron-${novoAberto ? "up" : "down"} me-1`} />Novo mailing de teste
                  </button>
                  {novoAberto && (
                    <form
                      className="mt-1"
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        executar("criar", async () => {
                          const n = Math.max(1, Math.min(20, Number(novo.tentativas_max) || 1));
                          const r = await api("POST", "/mailings", {
                            nome: novo.nome,
                            atendente_id: Number(novo.atendente_id),
                            numero_id: Number(novo.numero_id),
                            tentativas_max: n,
                            intervalos_min: Array(n - 1).fill(Math.max(1, Number(novo.intervalo) || 1)),
                            horario_ini: novo.horario_ini,
                            horario_fim: novo.horario_fim,
                            fuso: "America/Sao_Paulo",
                          });
                          await carregarRecursos();
                          if (r?.mailing?.id) setMailingSel(String(r.mailing.id));
                          setNovoAberto(false);
                        }, "Mailing criado (pausado).");
                      }}
                    >
                      <label style={lbl}>Nome</label>
                      <input className="form-control form-control-sm mb-2" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} required maxLength={200} />
                      <div className="d-flex gap-2">
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Atendente</label>
                          <select className="form-select form-select-sm mb-2" value={novo.atendente_id} onChange={(e) => setNovo({ ...novo, atendente_id: e.target.value })} required>
                            <option value="">Escolha…</option>
                            {(recursos.atendentes || []).map((a) => (
                              <option key={a.id} value={a.id}>
                                {rotulo(a, ["nome", "apelido", "ramal", "email"])}{a.ramal ? ` (ramal ${a.ramal})` : ""}{a.ativo === false ? " — inativo" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Número de saída</label>
                          <select className="form-select form-select-sm mb-2" value={novo.numero_id} onChange={(e) => setNovo({ ...novo, numero_id: e.target.value })} required>
                            <option value="">Escolha…</option>
                            {(recursos.numeros || []).map((n) => (
                              <option key={n.id} value={n.id}>
                                {rotulo(n, ["numero", "nome", "descricao"])}{n.ativo === false ? " — inativo" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Tentativas</label>
                          <input type="number" min={1} max={20} className="form-control form-control-sm mb-2" value={novo.tentativas_max}
                            onChange={(e) => setNovo({ ...novo, tentativas_max: Number(e.target.value) })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Intervalo (min)</label>
                          <input type="number" min={1} className="form-control form-control-sm mb-2" value={novo.intervalo}
                            onChange={(e) => setNovo({ ...novo, intervalo: Number(e.target.value) })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Das</label>
                          <input type="time" className="form-control form-control-sm mb-2" value={novo.horario_ini} onChange={(e) => setNovo({ ...novo, horario_ini: e.target.value })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Até</label>
                          <input type="time" className="form-control form-control-sm mb-2" value={novo.horario_fim} onChange={(e) => setNovo({ ...novo, horario_fim: e.target.value })} />
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: textMuted }} className="mb-2">
                        Até {novo.tentativas_max} ligação(ões) por contato, com pelo menos {novo.intervalo} min entre elas, das {novo.horario_ini} às {novo.horario_fim} (Brasília).
                        Atender encerra a insistência; caixa postal não conta como atendida. Nasce pausado, e a política não muda depois:
                        outra política pede outro mailing.
                      </div>
                      <div style={{ fontSize: 12, color: textMuted }} className="mb-2">
                        <i className="ti ti-info-circle me-1" />
                        Na conta de teste do Eduardo, o guia indica o atendente do <b>ramal 1001</b> (id 23) e a origem id 27. Confira na lista.
                      </div>
                      <button type="submit" className="btn btn-sm btn-primary" disabled={!!ocupado}>
                        {ocupado === "criar" ? "Criando…" : "Criar mailing (pausado)"}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>

            {/* 3. Etapas */}
            <div style={card}>
              <Titulo n={3} icone="git-branch" texto="Etapas que ligam" />
              <div className="d-flex gap-2 flex-wrap">
                <div style={{ flex: "1 1 140px" }}>
                  <label style={lbl}>Funil</label>
                  <select className="form-select form-select-sm mb-2" value={funilSel} onChange={(e) => { setFunilSel(e.target.value); setEtapaSel(""); }}>
                    {funis.map(([id, nome]) => (
                      <option key={id} value={id}>{nome}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={lbl}>Etapa</label>
                  <select className="form-select form-select-sm mb-2" value={etapaSel} onChange={(e) => setEtapaSel(e.target.value)}>
                    <option value="">Escolha…</option>
                    {etapasDoFunil.map((e) => (
                      <option key={e.id_etapa} value={e.id_etapa}>
                        {e.nome_etapa} ({e.cards} card{e.cards === 1 ? "" : "s"}){e.mailing_id ? ` → #${e.mailing_id}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={lbl}>Mailing</label>
                  {mailings.length ? (
                    <select className="form-select form-select-sm mb-2" value={mapaMailing} onChange={(e) => setMapaMailing(e.target.value)}>
                      <option value="">Escolha…</option>
                      {mailings.map((m) => (
                        <option key={m.id} value={m.id}>#{m.id} {rotulo(m, ["nome"])}</option>
                      ))}
                    </select>
                  ) : (
                    // só com a chave de integração não dá para listar mailings: o ID vem da gestão da discadora
                    <input className="form-control form-control-sm mb-2" inputMode="numeric" placeholder="ID informado pela gestão"
                      value={mapaMailing} onChange={(e) => setMapaMailing(e.target.value.replace(/\D/g, ""))} />
                  )}
                </div>
              </div>
              <button type="button" className="btn btn-sm btn-primary" disabled={!etapaSel || !mapaMailing || !!ocupado}
                onClick={() => executar("mapa", async () => {
                  await api("POST", "/mapa", { id_etapa: Number(etapaSel), mailing_id: Number(mapaMailing) });
                  await carregarEtapas();
                }, "Etapa ligada. Quem já estava nela não é inscrito: só quem entrar a partir de agora.")}>
                <i className="ti ti-link me-1" />Ligar etapa ao mailing
              </button>

              <div className="mt-3">
                {!mapas.length ? (
                  <div style={{ fontSize: 13, color: textMuted }}>Nenhuma etapa ligada ainda.</div>
                ) : (
                  mapas.map((m) => (
                    <div key={m.id} className="d-flex justify-content-between align-items-start gap-2 mb-2 p-2" style={{ border: `1px solid ${border}`, borderRadius: 8 }}>
                      <div style={{ fontSize: 13, color: textMain }}>
                        <div style={{ fontWeight: 600 }}>
                          {m.nome_funil_pai} › {m.nome_etapa} <i className="ti ti-arrow-right mx-1" style={{ color: textMuted }} /> mailing #{m.mailing_id}
                          {m.mailing_nome ? ` ${m.mailing_nome}` : ""}
                        </div>
                        <div style={{ fontSize: 12, color: textMuted }}>
                          {m.na_etapa_agora} na etapa agora · {m.ja_estavam} já estavam (não ligam)
                          {Object.keys(m.inscricoes || {}).length > 0 &&
                            " · " + Object.entries(m.inscricoes).map(([k, v]) => `${ESTADOS[k]?.rotulo || k}: ${v}`).join(", ")}
                        </div>
                        {(() => {
                          const rg = regras[m.id_etapa];
                          const g = GATILHOS_WHATS.find((x) => x.v === rg?.gatilho);
                          return (
                            <button
                              type="button"
                              className="btn btn-sm mt-2 me-2 d-inline-flex align-items-center gap-1"
                              style={{
                                fontSize: 12, fontWeight: 600, borderRadius: 999, padding: "2px 10px",
                                color: rg?.ativo ? "#16a34a" : textMuted,
                                background: rg?.ativo ? "#16a34a14" : "transparent",
                                border: `1px solid ${rg?.ativo ? "#16a34a55" : border}`,
                              }}
                              title="Configurar o WhatsApp quando a discadora não conseguir contato"
                              onClick={() => {
                                const base = rg || { ...(regraPadrao || { gatilho: "primeira", resultados: ["nao_atendida", "ocupado"], mensagem: "" }), ativo: false };
                                setEditandoRegra({ ...base, id_etapa: m.id_etapa, nome_etapa: m.nome_etapa } as RegraWhats & { nome_etapa: string });
                              }}
                            >
                              <i className="ti ti-brand-whatsapp" />
                              {rg?.ativo ? `WhatsApp sem contato: ${g?.titulo.toLowerCase()}` : "WhatsApp sem contato: desligado"}
                              <i className="ti ti-settings" style={{ opacity: 0.7 }} />
                            </button>
                          );
                        })()}
                        <button
                          type="button"
                          className="btn btn-sm mt-2 d-inline-flex align-items-center gap-1"
                          style={{
                            fontSize: 12, fontWeight: 600, borderRadius: 999, padding: "2px 10px",
                            color: m.campos?.length ? "#2563eb" : textMuted,
                            background: m.campos?.length ? "#2563eb14" : "transparent",
                            border: `1px solid ${m.campos?.length ? "#2563eb55" : border}`,
                          }}
                          title="Escolher os campos do card que vão junto para a discadora"
                          onClick={() => abrirCampos(m.id_etapa, m.nome_etapa)}
                        >
                          <i className="ti ti-forms" />
                          {m.campos_do_padrao
                            ? `Campos do card: padrão da conta (${m.campos?.length || 0})`
                            : m.campos?.length
                              ? `Campos do card: ${m.campos.length}`
                              : "Campos do card: nenhum"}
                          <i className="ti ti-settings" style={{ opacity: 0.7 }} />
                        </button>
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-danger" disabled={!!ocupado} title="Desligar esta etapa da discadora"
                        onClick={() => executar("desligar", async () => { await api("DELETE", `/mapa/${m.id}`); await carregarEtapas(); }, "Etapa desligada.")}>
                        <i className="ti ti-unlink" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Trava de teste */}
              <div className="mt-3 pt-3" style={{ borderTop: `1px dashed ${border}` }}>
                <span style={lbl}>
                  <i className="ti ti-shield-lock me-1" />
                  Telefones de teste (só eles são inscritos)
                </span>
                <div className="d-flex flex-wrap gap-2 my-2">
                  {(estado?.fones_autorizados || []).map((f) => (
                    <Badge key={f} cor="#2563eb">
                      {formatFone(f)}
                      <button type="button" className="btn p-0 ms-1" style={{ lineHeight: 1, color: "#2563eb", fontSize: 12 }} title="Tirar da lista"
                        onClick={() => executar("fones", () => api("POST", "/fones", { fones: (estado?.fones_autorizados || []).filter((x) => x !== f) }))}>
                        <i className="ti ti-x" />
                      </button>
                    </Badge>
                  ))}
                  {estado && !estado.fones_autorizados.length && (
                    <Badge cor="#dc2626">lista vazia: QUALQUER card que entrar na etapa será inscrito</Badge>
                  )}
                </div>
                <form className="d-flex gap-2"
                  onSubmit={(ev) => {
                    ev.preventDefault();
                    executar("fones", async () => {
                      await api("POST", "/fones", { fones: [...(estado?.fones_autorizados || []), foneNovo] });
                      setFoneNovo("");
                    });
                  }}>
                  <input className="form-control form-control-sm" placeholder="(41) 99999-9999" value={foneNovo} onChange={(e) => setFoneNovo(e.target.value)} />
                  <button type="submit" className="btn btn-sm btn-outline-primary" disabled={!foneNovo.trim() || !!ocupado}>Adicionar</button>
                </form>
              </div>
            </div>
          </div>

          {/* 4. Inscrições */}
          <div style={{ ...card, padding: 0 }} className="mb-3">
            <div style={{ padding: "14px 16px 0" }}>
              <Titulo
                n={4}
                icone="users"
                texto="Inscrições"
                dir={
                  <div className="d-flex flex-wrap gap-1">
                    {Object.entries(cont).map(([k, v]) => (
                      <Badge key={k} cor={ESTADOS[k]?.cor || "#6b7280"}>{ESTADOS[k]?.rotulo || k}: {v}</Badge>
                    ))}
                  </div>
                }
              />
            </div>
            <div style={{ overflow: "auto", maxHeight: 360 }}>
              {!inscricoes.length ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>
                  Ninguém entrou numa etapa ligada ainda. Arraste um card de teste para a etapa e aguarde até 5 segundos.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Entrou</th>
                      <th style={th}>Contato</th>
                      <th style={th}>Etapa</th>
                      <th style={th}>Mailing / lead</th>
                      <th style={th}>Situação</th>
                      <th style={th}>Discadora</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {inscricoes.map((i) => {
                      const st = ESTADOS[i.estado] || { rotulo: i.estado, cor: "#6b7280", dica: "" };
                      const res = i.ultimo_resultado ? RESULTADOS[i.ultimo_resultado] || { rotulo: i.ultimo_resultado, cor: "#6b7280" } : null;
                      return (
                        <tr key={i.id}>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{formatData(i.criado_em, true)}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{i.nome}</div>
                            <div style={{ fontSize: 12, color: textMuted, whiteSpace: "nowrap" }}>{formatFone(i.telefone_e164 || i.fone)} · card {i.card_id}</div>
                          </td>
                          <td style={td}>{i.nome_etapa}</td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            #{i.mailing_id}
                            {i.lead_id ? <span style={{ color: textMuted }}> · lead {i.lead_id}</span> : null}
                            {i.duplicado ? <div style={{ fontSize: 11, color: textMuted }}>repetição idêntica</div> : null}
                          </td>
                          <td style={{ ...td, maxWidth: 280 }}>
                            <Badge cor={st.cor} dica={st.dica}>{st.rotulo}</Badge>
                            {i.erro && <div style={{ fontSize: 11.5, color: textMuted, marginTop: 3 }}>{i.erro}</div>}
                          </td>
                          <td style={td}>
                            {res ? (
                              <>
                                <Badge cor={res.cor}>{res.rotulo}</Badge>
                                <div style={{ fontSize: 11.5, color: textMuted, marginTop: 3 }}>
                                  {i.tentativas_discadora} tentativa(s){i.teve_pna ? " · teve 1º não atendimento" : ""}
                                </div>
                                {i.whats_estado && (
                                  <div style={{ fontSize: 11.5, marginTop: 3, color: (WHATS_ESTADO[i.whats_estado] || { cor: textMuted }).cor, fontWeight: 600 }}>
                                    <i className="ti ti-brand-whatsapp me-1" />
                                    {(WHATS_ESTADO[i.whats_estado] || { rotulo: i.whats_estado }).rotulo}
                                    {i.whats_enviados && i.whats_enviados > 1 ? ` (${i.whats_enviados})` : ""}
                                    {i.whats_em ? ` · ${formatData(i.whats_em, true)}` : ""}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span style={{ fontSize: 12, color: textMuted }}>{i.estado === "enviado" ? "aguardando ligação" : "—"}</span>
                            )}
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            {i.estado === "erro" && (
                              <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!ocupado} title="Reenviar com a mesma referência"
                                onClick={() => executar("reenviar", () => api("POST", `/inscricoes/${i.id}/reenviar`, {}), "Recolocada na fila.")}>
                                <i className="ti ti-send" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* 5. Resultados */}
          <div style={{ ...card, padding: 0 }} className="mb-3">
            <div style={{ padding: "14px 16px 0" }}>
              <Titulo n={5} icone="phone-check" texto="Resultados das tentativas" dir={<span style={{ fontSize: 12, color: textMuted }}>consulta a discadora a cada 5s</span>} />
            </div>
            <div style={{ overflow: "auto", maxHeight: 320 }}>
              {!resultados.length ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>
                  Nenhuma tentativa encerrada ainda. Cada resultado aparece quando a ligação termina.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Encerrada</th>
                      <th style={th}>Contato</th>
                      <th style={th}>Tentativa</th>
                      <th style={th}>Resultado</th>
                      <th style={th}>Mailing / lead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.map((r) => {
                      const res = RESULTADOS[r.resultado] || { rotulo: r.resultado, cor: "#6b7280" };
                      return (
                        <tr key={r.id}>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{formatData(r.encerrada_em, true)}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{r.nome || "—"}</div>
                            <div style={{ fontSize: 12, color: textMuted }}>{formatFone(r.telefone || r.fone)}{r.card_id ? ` · card ${r.card_id}` : ""}</div>
                          </td>
                          <td style={td}>{r.tentativa_numero === 0 ? "ainda não discado" : `${r.tentativa_numero}ª`}</td>
                          <td style={td}>
                            <Badge cor={res.cor}>{res.rotulo}</Badge>
                            {!!r.primeira_nao_atendida && <div style={{ fontSize: 11.5, color: "#d97706", marginTop: 3 }}>1º não atendimento</div>}
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>#{r.mailing_id} · lead {r.lead_id}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* 6. Conversas */}
          <div style={{ ...card, padding: 0 }} className="mb-3">
            <div style={{ padding: "14px 16px 0" }}>
              <Titulo
                n={6}
                icone="microphone"
                texto="Conversas: gravação e transcrição"
                dir={
                  credOk && !contaChave && (
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={carregandoConversas} onClick={carregarConversas}>
                      <i className="ti ti-refresh me-1" />
                      {carregandoConversas ? "Buscando…" : "Atualizar"}
                    </button>
                  )
                }
              />
            </div>
            <div style={{ overflow: "auto", maxHeight: 340 }}>
              {contaChave ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>
                  Gravações e transcrições ainda não têm rota com a chave de integração (a discadora vai entregar depois). Para esta
                  conta, ficam no painel da discadora.
                </div>
              ) : !credOk ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>Salve a credencial primeiro.</div>
              ) : erroConversas ? (
                <div className="p-3" style={{ color: "#dc2626", fontSize: 13 }}>
                  <i className="ti ti-alert-triangle me-1" />
                  {erroConversas}
                </div>
              ) : conversas === null ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>Carregando da discadora…</div>
              ) : !conversas.length ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>
                  Nenhuma conversa gravada nesta conta da discadora ainda.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Quando</th>
                      <th style={th}>Contato</th>
                      <th style={th}>Origem</th>
                      <th style={th}>Duração</th>
                      <th style={th}>Situação</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversas.map((cv) => {
                      // formato real: `transcrita` (bool) em vez de status; `classificacao` = humano / caixa postal…
                      const sit =
                        typeof cv.transcrita === "boolean"
                          ? cv.transcrita
                            ? { cor: "#16a34a", texto: "transcrita", dica: "Transcrição pronta; abra para ver as análises" }
                            : { cor: "#2563eb", texto: "processando", dica: "Processamento assíncrono: pendente não é falha" }
                          : situacaoTarefa(primeiro(cv, CONV_STATUS));
                      const fone = primeiro(cv, CONV_FONE);
                      const mailing = cv.mailing_id || cv.crm?.mailing_id;
                      return (
                        <tr key={cv.id}>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{formatData(primeiro(cv, CONV_DATA), true)}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{cv.crm?.nome || (fone ? formatFone(fone) : "—")}</div>
                            <div style={{ fontSize: 12, color: textMuted, whiteSpace: "nowrap" }}>
                              {cv.crm?.card_id ? `card ${cv.crm.card_id}` : cv.tentativa_id ? `tentativa ${cv.tentativa_id}` : ""}
                            </div>
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            {mailing ? `mailing #${mailing}` : humano(primeiro(cv, ["origem", "tipo"]) || "—")}
                            {cv.classificacao && <div style={{ fontSize: 11.5, color: textMuted }}>{humano(cv.classificacao)}</div>}
                            {!cv.crm && <div style={{ fontSize: 11, color: textMuted }}>sem card deste CRM</div>}
                          </td>
                          <td style={td}>{formatDuracao(primeiro(cv, CONV_DURACAO))}</td>
                          <td style={td}>
                            <Badge cor={sit.cor} dica={sit.dica}>{sit.texto}</Badge>
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => abrirConversa(Number(cv.id))}>
                              <i className="ti ti-file-text me-1" />
                              Abrir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: "8px 16px 12px", fontSize: 12, color: textMuted }}>
              <i className="ti ti-lock me-1" />
              Vem direto da discadora; nada fica guardado no CRM. O áudio passa pelo servidor do CRM e não tem link público.
            </div>
          </div>

          {/* Botão Ligar (card do Kanban): usuário do CRM → atendente da discadora + campos padrão + últimas ligações */}
          {contaChave && (
            <div style={card} className="mb-3">
              <Titulo
                icone="phone-outgoing"
                texto="Botão Ligar (chat e card do Kanban)"
                dir={
                  estado?.conta?.chave_configurada ? (
                    <Badge cor="#16a34a">pronto quando houver atendente</Badge>
                  ) : (
                    <Badge cor="#d97706">depende da chave</Badge>
                  )
                }
              />
              <div style={{ fontSize: 12.5, color: textMuted }} className="mb-2">
                Liga um contato por vez, pelo topo da conversa no chat ou pelo card do Kanban (Lista de Leads). O CRM pede à discadora
                um link de uso único, válido por 10 min, e abre a ficha no fone do atendente. Esse botão <b>não inscreve no mailing</b>.
                Ele só aparece para quem tem um atendente da discadora ligado abaixo (o ID vem da gestão; o atendente entra com a
                própria conta e ramal).
              </div>
              <div
                className="d-flex align-items-start gap-2 mb-3 p-2"
                style={{ border: `1px solid ${estado?.ligar_todos ? "#16a34a55" : border}`, borderRadius: 8 }}
              >
                <span className="form-check form-switch m-0 pt-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    style={{ cursor: "pointer" }}
                    checked={!!estado?.ligar_todos}
                    disabled={!!ocupado}
                    onChange={(e) => {
                      const ativo = e.target.checked;
                      executar("ligar_todos", () => api("POST", "/ligar/todos", { ativo }),
                        ativo ? "Botão Ligar liberado para qualquer contato." : "Botão Ligar de volta só para os telefones de teste.");
                    }}
                  />
                </span>
                <div style={{ fontSize: 13, color: textMain }}>
                  <b>Ligar para qualquer contato da conta</b>
                  <div style={{ fontSize: 12, color: textMuted }}>
                    {estado?.ligar_todos
                      ? "Ligado: o botão Ligar liga para qualquer contato desta conta."
                      : "Desligado: o botão Ligar só liga para os telefones de teste (lista no quadro “3 · Etapas que ligam”)."}{" "}
                    Não muda a lista de teste do mailing automático.
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
                <div>
                  <span style={lbl}>Usuários do CRM → atendente da discadora</span>
                  {atendentesCrm === null ? (
                    <div style={{ fontSize: 13, color: textMuted }}>Carregando…</div>
                  ) : !atendentesCrm.length ? (
                    <div style={{ fontSize: 13, color: textMuted }}>Nenhum usuário nesta conta.</div>
                  ) : (
                    atendentesCrm.map((u) => {
                      const valor = atendEdit[u.id_user] ?? (u.atendente_id ? String(u.atendente_id) : "");
                      const mudou = valor !== (u.atendente_id ? String(u.atendente_id) : "");
                      return (
                        <div key={u.id_user} className="d-flex align-items-center gap-2 py-1" style={{ borderBottom: `1px solid ${border}`, fontSize: 13, color: textMain }}>
                          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {u.nome || `Usuário ${u.id_user}`}
                          </span>
                          <input
                            className="form-control form-control-sm"
                            style={{ width: 110 }}
                            inputMode="numeric"
                            placeholder="ID atendente"
                            value={valor}
                            onChange={(e) => setAtendEdit({ ...atendEdit, [u.id_user]: e.target.value.replace(/\D/g, "") })}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={!mudou || !!ocupado}
                            onClick={() =>
                              executar("atendente", async () => {
                                await api("POST", "/atendentes-crm", { id_user: u.id_user, atendente_id: valor ? Number(valor) : null });
                                const resto = { ...atendEdit };
                                delete resto[u.id_user];
                                setAtendEdit(resto);
                                await carregarAtendentes();
                              }, valor ? `${u.nome || "Usuário"} ligado ao atendente ${valor}.` : `${u.nome || "Usuário"} sem botão Ligar.`)
                            }
                          >
                            Salvar
                          </button>
                        </div>
                      );
                    })
                  )}
                  <button type="button" className="btn btn-sm btn-outline-secondary mt-3" onClick={() => abrirCampos(null, "Padrão da conta")}>
                    <i className="ti ti-forms me-1" />
                    Campos padrão da conta
                  </button>
                  <div style={{ fontSize: 11.5, color: textMuted }} className="mt-1">
                    Vão na ficha do botão Ligar e nas etapas sem configuração própria.
                  </div>
                </div>
                <div>
                  <span style={lbl}>Últimas ligações pedidas</span>
                  {!ligacoes.length ? (
                    <div style={{ fontSize: 13, color: textMuted }}>Nenhuma ainda.</div>
                  ) : (
                    ligacoes.map((l) => {
                      const st = ROTULO_LIGACAO[l.estado] || { rotulo: l.estado, cor: "#6b7280" };
                      return (
                        <div key={l.id} className="d-flex align-items-center gap-2 py-1" style={{ borderBottom: `1px solid ${border}`, fontSize: 12.5, color: textMain }}>
                          <span style={{ color: textMuted, whiteSpace: "nowrap" }}>{formatData(l.criado_em, true)}</span>
                          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {l.nome || (l.fone ? formatFone(l.fone) : "—")}
                            {l.card_id ? <span style={{ color: textMuted }}> · card {l.card_id}</span> : null}
                          </span>
                          <Badge cor={st.cor} dica={l.erro || undefined}>{st.rotulo}</Badge>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Registro */}
          <div style={card} className="mb-3">
            <Titulo
              icone="history"
              texto="Registro"
              dir={
                <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setRegistroAberto(!registroAberto)}>
                  {registroAberto ? "Esconder" : "Mostrar"}
                </button>
              }
            />
            {registroAberto && (
              <div style={{ maxHeight: 260, overflow: "auto", fontSize: 12.5 }}>
                {!eventos.length ? (
                  <div style={{ color: textMuted }}>Nada registrado ainda.</div>
                ) : (
                  eventos.map((e) => (
                    <div key={e.id} className="d-flex gap-2 py-1" style={{ borderBottom: `1px solid ${border}` }}>
                      <span style={{ color: textMuted, whiteSpace: "nowrap" }}>{formatData(e.quando, true)}</span>
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: NIVEL_COR[e.nivel] || "#6b7280", marginTop: 6, flex: "0 0 8px" }} />
                      <span style={{ color: textMain }}>{e.msg}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Campos do card que vão junto para a discadora */}
      {editandoCampos && (() => {
        const ec = editandoCampos;
        const d = ec.dados;
        const max = d?.maximo || 25;
        const marcado = (id: number) => ec.escolhidos.some((x) => x.id === id);
        const sugestao = (c: CampoCard) => c.sugestao || c.label;
        const alternar = (c: CampoCard) =>
          setEditandoCampos({
            ...ec,
            escolhidos: marcado(c.id)
              ? ec.escolhidos.filter((x) => x.id !== c.id)
              : ec.escolhidos.length >= max
                ? ec.escolhidos
                : [...ec.escolhidos, { id: c.id, chave: sugestao(c) }],
          });
        const renomear = (id: number, chave: string) =>
          setEditandoCampos({ ...ec, escolhidos: ec.escolhidos.map((x) => (x.id === id ? { ...x, chave } : x)) });
        const reservadas = d?.reservadas || ["origem", "funil", "etapa", "card_id"];
        const problemaChave = (e: EscolhaCampo): string | null => {
          const k = e.chave.trim();
          if (!k) return "dê um nome";
          if (k.length > 80) return "até 80 caracteres";
          if (reservadas.includes(k)) return "nome reservado do CRM";
          if (ec.escolhidos.filter((x) => x.chave.trim().toLowerCase() === k.toLowerCase()).length > 1) return "nome repetido";
          return null;
        };
        const temProblema = ec.escolhidos.some((e) => !!problemaChave(e));
        const grupos = new Map<string, CampoCard[]>();
        (d?.disponiveis || []).forEach((c) => {
          const k = c.grupo || "Campos";
          grupos.set(k, [...(grupos.get(k) || []), c]);
        });
        const valorAmostra = (id: number) => d?.amostra?.valores?.[String(id)];
        const vaiNaAmostra = ec.escolhidos.filter((e) => !!valorAmostra(e.id));
        const onde = ec.id_etapa ? `na etapa "${ec.nome_etapa}"` : "no padrão da conta";
        const salvar = () =>
          executar("campos", async () => {
            await api("POST", "/campos", {
              id_etapa: ec.id_etapa ?? "padrao",
              campos: ec.escolhidos.map((e) => ({ id: e.id, chave: e.chave.trim() })),
            });
            setEditandoCampos(null);
          }, ec.escolhidos.length
            ? `${ec.escolhidos.length} campo(s) do card vão junto para a discadora ${onde}.`
            : `Nenhum campo do card vai para a discadora ${onde}.`);
        const voltarAoPadrao = () =>
          executar("campos", async () => {
            await api("POST", "/campos", { id_etapa: ec.id_etapa, usar_padrao: true });
            setEditandoCampos(null);
          }, `A etapa "${ec.nome_etapa}" volta a usar os campos padrão da conta.`);
        return (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => setEditandoCampos(null)}
          >
            <div style={{ ...card, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 50px rgba(0,0,0,.3)" }} onClick={(e) => e.stopPropagation()}>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: textMain }}>
                    <i className="ti ti-forms me-2" style={{ color: "#2563eb" }} />
                    Campos do card que vão para a discadora
                  </div>
                  <div style={{ fontSize: 12.5, color: textMuted }}>
                    {ec.id_etapa ? `Etapa "${ec.nome_etapa}"` : "Padrão da conta: botão Ligar e etapas sem configuração própria"}
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditandoCampos(null)}><i className="ti ti-x" /></button>
              </div>
              <div style={{ fontSize: 12.5, color: textMuted }} className="mb-3">
                O campo é escolhido pelo <b>ID</b> e vai com o <b>nome na discadora</b> que você definir (renomear no CRM não quebra).
                Só vai o que estiver <b>preenchido</b> no card; "0" e "false" contam como valor. <b>cidade</b>, <b>uf</b> e{" "}
                <b>observacao</b> aparecem em destaque na ficha do atendente. Vale a partir de agora; o que já foi enviado não muda.
              </div>
              {d?.usa_padrao && (
                <div className="mb-3 p-2" style={{ fontSize: 12.5, color: textMain, border: `1px dashed ${border}`, borderRadius: 10 }}>
                  Esta etapa usa hoje os <b>campos padrão da conta</b> (marcados abaixo). Salvar aqui cria uma configuração só desta etapa.
                </div>
              )}

              {ec.carregando && <div style={{ fontSize: 13, color: textMuted }}>Carregando os campos do card…</div>}
              {ec.erro && <div style={{ fontSize: 13, color: "#dc2626" }}>{ec.erro}</div>}
              {d && !d.disponiveis.length && (
                <div style={{ fontSize: 13, color: textMain, border: `1px dashed ${border}`, borderRadius: 10, padding: 12 }}>
                  Este funil ainda não tem campos de card. Crie os campos no editor de card do CRM (grupo do time ou global) e volte aqui.
                </div>
              )}
              {d && d.disponiveis.length > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={lbl}>{ec.escolhidos.length} de {max} campos escolhidos</span>
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-sm btn-link p-0" style={{ fontSize: 12 }}
                        onClick={() => setEditandoCampos({ ...ec, escolhidos: d.disponiveis.slice(0, max).map((c) => ({ id: c.id, chave: sugestao(c) })) })}>marcar todos</button>
                      <button type="button" className="btn btn-sm btn-link p-0" style={{ fontSize: 12 }}
                        onClick={() => setEditandoCampos({ ...ec, escolhidos: [] })}>limpar</button>
                    </div>
                  </div>
                  {Array.from(grupos.entries()).map(([nomeGrupo, lista]) => (
                    <div key={nomeGrupo} className="mb-2">
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: textMuted, textTransform: "uppercase", letterSpacing: 0.3 }} className="mb-1">{nomeGrupo}</div>
                      {lista.map((c) => {
                        const v = valorAmostra(c.id);
                        const esc = ec.escolhidos.find((x) => x.id === c.id);
                        const prob = esc ? problemaChave(esc) : null;
                        return (
                          <label key={c.id} className="d-flex align-items-start gap-2 py-1" style={{ cursor: "pointer", borderBottom: `1px solid ${border}`, color: textMain, fontSize: 13 }}>
                            <input type="checkbox" className="form-check-input mt-1" checked={marcado(c.id)} onChange={() => alternar(c)}
                              disabled={!marcado(c.id) && ec.escolhidos.length >= max} />
                            <span style={{ flex: 1, minWidth: 0 }}>
                              {c.label}
                              <span style={{ fontSize: 10.5, color: textMuted, marginLeft: 6 }}>
                                #{c.id}{c.global ? " · global" : ""}{c.time ? ` · ${c.time}` : ""}
                              </span>
                              {esc && (
                                <span className="d-flex align-items-center gap-2 mt-1" onClick={(e) => e.preventDefault()}>
                                  <span style={{ fontSize: 11.5, color: textMuted, whiteSpace: "nowrap" }}>nome na discadora</span>
                                  <input
                                    className="form-control form-control-sm"
                                    style={{ maxWidth: 260, borderColor: prob ? "#dc2626" : undefined }}
                                    value={esc.chave}
                                    maxLength={80}
                                    onChange={(e) => renomear(c.id, e.target.value)}
                                  />
                                  {prob && <span style={{ fontSize: 11.5, color: "#dc2626" }}>{prob}</span>}
                                </span>
                              )}
                              {d.amostra && (
                                <span style={{ display: "block", fontSize: 11.5, color: textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 560 }}>
                                  {v ? `no card ${d.amostra.card_id}: ${v}` : `vazio no card ${d.amostra.card_id}`}
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                  <div className="mt-3 p-2" style={{ background: isDark ? "#172229" : "#f8fafc", border: `1px solid ${border}`, borderRadius: 10, fontSize: 12.5, color: textMain }}>
                    {d.amostra ? (
                      <>
                        <div style={{ fontWeight: 600 }} className="mb-1">
                          Exemplo com o card {d.amostra.card_id}{d.amostra.nome ? ` (${d.amostra.nome})` : ""}, que está na etapa agora:
                        </div>
                        {vaiNaAmostra.length ? (
                          vaiNaAmostra.map((c) => (
                            <div key={c.id} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              <span style={{ color: textMuted }}>campos.{c.chave}:</span> {valorAmostra(c.id)}
                            </div>
                          ))
                        ) : (
                          <div style={{ color: textMuted }}>Nenhum dos campos escolhidos está preenchido nesse card: ele iria só com nome e telefone.</div>
                        )}
                      </>
                    ) : (
                      <div style={{ color: textMuted }}>Nenhum card na etapa agora para mostrar um exemplo.</div>
                    )}
                  </div>
                </>
              )}
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditandoCampos(null)}>Cancelar</button>
                {ec.id_etapa && d && !d.usa_padrao && (
                  <button type="button" className="btn btn-sm btn-outline-secondary me-auto" disabled={!!ocupado} onClick={voltarAoPadrao}
                    title="Apaga a configuração desta etapa; ela passa a usar os campos padrão da conta">
                    Usar o padrão da conta
                  </button>
                )}
                <button type="button" className="btn btn-sm btn-primary" disabled={!d || !!ocupado || temProblema} onClick={salvar}>
                  {ocupado === "campos" ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* WhatsApp quando não conseguir contato */}
      {editandoRegra && (() => {
        const r = editandoRegra;
        const previa = renderWhats(r.mensagem, { primeiro_nome: "João", nome: "João Silva", tentativa: "1", etapa: r.nome_etapa });
        const salvar = () =>
          executar("regra", async () => {
            await api("POST", "/regras", {
              id_etapa: r.id_etapa, ativo: r.ativo, gatilho: r.gatilho, resultados: r.resultados, mensagem: r.mensagem,
            });
            setEditandoRegra(null);
          }, r.ativo ? `WhatsApp ligado na etapa "${r.nome_etapa}".` : `Configuração salva (WhatsApp desligado em "${r.nome_etapa}").`);
        const opcao = (ativo: boolean): React.CSSProperties => ({
          border: `1px solid ${ativo ? "#2563eb" : border}`, background: ativo ? (isDark ? "#1b2a3d" : "#eff6ff") : "transparent",
          borderRadius: 10, padding: "8px 10px", cursor: "pointer", textAlign: "left", color: textMain, width: "100%",
        });
        return (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => setEditandoRegra(null)}
          >
            <div style={{ ...card, maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 50px rgba(0,0,0,.3)" }} onClick={(e) => e.stopPropagation()}>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: textMain }}>
                    <i className="ti ti-brand-whatsapp me-2" style={{ color: "#16a34a" }} />
                    WhatsApp quando não conseguir contato
                  </div>
                  <div style={{ fontSize: 12.5, color: textMuted }}>Etapa "{r.nome_etapa}"</div>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditandoRegra(null)}><i className="ti ti-x" /></button>
              </div>

              <label className="d-flex align-items-center gap-2 mb-3" style={{ cursor: "pointer", color: textMain, fontWeight: 600, fontSize: 14 }}>
                <span className="form-check form-switch m-0">
                  <input className="form-check-input" type="checkbox" checked={r.ativo} onChange={(e) => setEditandoRegra({ ...r, ativo: e.target.checked })} />
                </span>
                {r.ativo ? "Ligado" : "Desligado"}
              </label>

              <span style={lbl}>Quando mandar</span>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8 }} className="mb-3">
                {GATILHOS_WHATS.map((g) => (
                  <button key={g.v} type="button" style={opcao(r.gatilho === g.v)} onClick={() => setEditandoRegra({ ...r, gatilho: g.v })}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      <i className={`ti ti-${r.gatilho === g.v ? "circle-dot" : "circle"} me-1`} style={{ color: "#2563eb" }} />
                      {g.titulo}
                    </div>
                    <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>{g.dica}</div>
                  </button>
                ))}
              </div>

              {r.gatilho === "pna" ? (
                <div className="mb-3" style={{ fontSize: 12.5, color: textMuted }}>
                  Conta só a tentativa que a discadora marcar como a <b>primeira efetivamente discada e não atendida</b>
                  (caixa postal entra aqui). Uma mensagem por inscrição.
                </div>
              ) : (
              <>
              <span style={lbl}>O que conta como "sem contato"</span>
              <div className="d-flex flex-wrap gap-3 mb-3">
                {SEM_CONTATO.map((x) => (
                  <label key={x.v} className="d-flex align-items-center gap-1" style={{ fontSize: 13, color: textMain, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={r.resultados.includes(x.v)}
                      onChange={(e) =>
                        setEditandoRegra({ ...r, resultados: e.target.checked ? [...r.resultados, x.v] : r.resultados.filter((y) => y !== x.v) })
                      }
                    />
                    {x.rotulo}
                  </label>
                ))}
              </div>
              </>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
                <div>
                  <span style={lbl}>Mensagem</span>
                  <textarea
                    className="form-control form-control-sm"
                    rows={5}
                    maxLength={1000}
                    value={r.mensagem}
                    onChange={(e) => setEditandoRegra({ ...r, mensagem: e.target.value })}
                  />
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {VARIAVEIS_WHATS.map((v) => (
                      <button key={v} type="button" className="btn btn-sm py-0 px-2" style={{ fontSize: 11.5, border: `1px solid ${border}`, color: textMain }}
                        onClick={() => setEditandoRegra({ ...r, mensagem: `${r.mensagem}${r.mensagem && !/\s$/.test(r.mensagem) ? " " : ""}${v}` })}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span style={lbl}>Como chega (exemplo com o João)</span>
                  <div style={{ background: isDark ? "#0b141a" : "#efeae2", borderRadius: 10, padding: "12px 12px 12px 30px", display: "flex", justifyContent: "flex-end" }}>
                    <div style={{
                      background: isDark ? "#005c4b" : "#d9fdd3", color: isDark ? "#e9edef" : "#111b21", borderRadius: "8px 0 8px 8px",
                      padding: "6px 9px 4px", fontSize: 13.5, lineHeight: 1.35, whiteSpace: "pre-wrap", wordBreak: "break-word",
                      boxShadow: "0 1px 0.5px rgba(11,20,26,.13)", maxWidth: "100%",
                    }}>
                      {previa || <span style={{ opacity: 0.6 }}>(mensagem vazia)</span>}
                      <div style={{ fontSize: 11, opacity: 0.6, textAlign: "right", marginTop: 2 }}>agora</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 p-2" style={{ fontSize: 12, color: textMuted, background: isDark ? "#172229" : "#f8fafc", borderRadius: 8 }}>
                <i className="ti ti-info-circle me-1" />
                Sai pelo WhatsApp do time no CRM e fica no histórico da conversa. A discadora não avisa sozinha: o CRM consulta os
                resultados a cada 5 segundos. Nunca repete a mesma mensagem, só conta ligação que chegou a ser discada e nada é
                retroativo (resultado com mais de 20 min não gera mensagem).
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditandoRegra(null)}>Cancelar</button>
                <button type="button" className="btn btn-sm btn-primary" disabled={!!ocupado || !r.mensagem.trim() || (r.gatilho !== "pna" && !r.resultados.length)} onClick={salvar}>
                  {ocupado === "regra" ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Confirmação de início */}
      {confirmarInicio && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setConfirmarInicio(null)}
        >
          <div style={{ ...card, maxWidth: 440, width: "100%", boxShadow: "0 20px 50px rgba(0,0,0,.3)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 16, color: textMain }} className="mb-2">
              <i className="ti ti-alert-triangle me-2" style={{ color: "#d97706" }} />
              Iniciar discagem?
            </div>
            <p style={{ fontSize: 13.5, color: textMain }}>
              O mailing <b>#{confirmarInicio.id} {rotulo(confirmarInicio, ["nome"])}</b> vai fazer <b>chamadas reais</b> para os contatos
              da lista ({inscritosNoMailing(Number(confirmarInicio.id))} inscrito(s) por esta área) e para quem entrar depois.
            </p>
            <p style={{ fontSize: 12.5, color: textMuted }}>
              Antes: o ramal do atendente precisa estar registrado e "Disponível para discagem automática" no painel. Para parar, use Pausar.
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmarInicio(null)}>Cancelar</button>
              <button type="button" className="btn btn-sm btn-success" disabled={!!ocupado}
                onClick={() => {
                  const m = confirmarInicio;
                  setConfirmarInicio(null);
                  executar("iniciar", async () => {
                    await api("POST", `/mailings/${m.id}/status`, { status: "rodando", confirmo: true });
                    await carregarRecursos();
                  }, `Mailing #${m.id} rodando. Pause quando terminar o teste.`);
                }}>
                Sim, iniciar discagem
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detalhe da conversa */}
      {conversaAberta && (() => {
        const d = conversaAberta.dados;
        // pode haver "transcricao" e "transcricao_status": fica a de conteúdo maior
        const chTrans = Object.keys(d || {})
          .filter((k) => /transcri/i.test(k))
          .sort((a, b) => JSON.stringify(d![b] ?? "").length - JSON.stringify(d![a] ?? "").length)[0];
        const falas = chTrans ? falasDa(d![chTrans]) : null;
        const ehEscalar = (v: any) => v === null || ["string", "number", "boolean"].includes(typeof v);
        const curtos: [string, any][] = [];
        const longos: [string, string][] = [];
        const blocos: [string, any][] = [];
        Object.entries(d || {}).forEach(([k, v]) => {
          if (k === "crm" || k === chTrans || CONV_OCULTOS.has(k) || v === null || v === undefined || v === "") return;
          if (ehEscalar(v)) {
            if (typeof v === "string" && v.length > 120) longos.push([k, v]);
            else curtos.push([k, v]);
          } else blocos.push([k, v]);
        });
        const ORDEM = ["criado_em", "duracao_s", "origem", "classificacao", "tentativa_id", "referencia", "id", "audio_bytes"];
        const pos = (k: string) => (ORDEM.indexOf(k) < 0 ? 99 : ORDEM.indexOf(k));
        curtos.sort((a, b) => pos(a[0]) - pos(b[0]));
        const valorCurto = (k: string, v: any) =>
          CONV_DURACAO.includes(k) ? formatDuracao(v)
            : k === "audio_bytes" && Number.isFinite(Number(v)) ? `${Math.max(1, Math.round(Number(v) / 1024))} KB`
            : CONV_FONE.includes(k) ? formatFone(String(v))
            : ehDataIso(v) ? formatData(v, true)
            : k === "origem" || k === "classificacao" ? humano(v)
            : String(v);
        // A tarefa "transcricao" repete o texto que já aparece em Transcrição: fica fora das análises.
        const itensDe = (v: any): Dict[] =>
          (Array.isArray(v) ? v.map((x) => (x && typeof x === "object" ? x : { resultado: x })) : Object.entries(v).map(([k, x]) => (x && typeof x === "object" && !Array.isArray(x) ? { nome: k, ...(x as Dict) } : { nome: k, resultado: x })))
            .filter((it: Dict) => !(chTrans && falas && it.tipo === "transcricao"));
        const caixa: React.CSSProperties = { border: `1px solid ${border}`, borderRadius: 8, padding: 10, marginBottom: 8 };
        const pre: React.CSSProperties = { whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12.5, color: textMain, margin: 0, fontFamily: "inherit" };
        return (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={() => setConversaAberta(null)}
          >
            <div
              style={{ ...card, maxWidth: 780, width: "100%", maxHeight: "88vh", overflow: "auto", boxShadow: "0 20px 50px rgba(0,0,0,.3)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                <div style={{ fontWeight: 700, fontSize: 16, color: textMain }}>
                  <i className="ti ti-microphone me-2" style={{ color: "#2563eb" }} />
                  Conversa #{conversaAberta.id}
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-outline-primary" disabled={conversaAberta.carregando}
                    onClick={() => abrirConversa(conversaAberta.id)} title="As análises são assíncronas: atualize para ver as que terminaram">
                    <i className="ti ti-refresh me-1" />{conversaAberta.carregando ? "Buscando…" : "Atualizar"}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConversaAberta(null)}>
                    <i className="ti ti-x" />
                  </button>
                </div>
              </div>

              {conversaAberta.erro && (
                <div className="mb-2" style={{ color: "#dc2626", fontSize: 13 }}>
                  <i className="ti ti-alert-triangle me-1" />{conversaAberta.erro}
                </div>
              )}
              {!d && conversaAberta.carregando && <div style={{ color: textMuted, fontSize: 13 }}>Carregando da discadora…</div>}

              {d && (
                <>
                  {d.crm && (
                    <div className="mb-2 p-2" style={{ ...caixa, background: isDark ? "#1b2a33" : "#f0f6ff", borderColor: isDark ? "#2a3942" : "#cfe0ff", fontSize: 13, color: textMain }}>
                      <i className="ti ti-id-badge-2 me-1" style={{ color: "#2563eb" }} />
                      <b>{d.crm.nome}</b> · card {d.crm.card_id} · {d.crm.nome_etapa}
                      {d.crm.tentativa_numero ? ` · ${d.crm.tentativa_numero}ª tentativa` : ""}
                      {d.crm.resultado ? ` · ${RESULTADOS[d.crm.resultado]?.rotulo || d.crm.resultado}` : ""}
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "6px 14px" }} className="mb-3">
                    {curtos.map(([k, v]) => (
                      <div key={k}>
                        <span style={lbl}>{humano(k)}</span>
                        <span style={{ fontSize: 13, color: textMain, wordBreak: "break-word" }}>{valorCurto(k, v)}</span>
                      </div>
                    ))}
                  </div>

                  <span style={lbl}>Gravação</span>
                  <audio controls preload="none" src={urlAudio(conversaAberta.id)} style={{ width: "100%", marginBottom: 4 }} />
                  <div style={{ fontSize: 11.5, color: textMuted }} className="mb-3">
                    Se não tocar, a gravação ainda não está disponível ou esta chamada não foi gravada.
                  </div>

                  <span style={lbl}>Transcrição</span>
                  <div style={{ ...caixa, maxHeight: 260, overflow: "auto" }}>
                    {!falas ? (
                      <span style={{ fontSize: 12.5, color: textMuted }}>Ainda não disponível (o processamento é assíncrono).</span>
                    ) : typeof falas === "string" ? (
                      <pre style={pre}>{falas}</pre>
                    ) : (
                      falas.map((f, i) => (
                        <div key={i} style={{ fontSize: 12.5, color: textMain, marginBottom: 4 }}>
                          {f.quem && <b style={{ color: /atend|agent/i.test(f.quem) ? "#2563eb" : "#16a34a" }}>{f.quem}: </b>}
                          {f.texto}
                        </div>
                      ))
                    )}
                  </div>

                  {(longos.length > 0 || blocos.length > 0) && <span style={lbl}>Análises</span>}
                  {longos.map(([k, v]) => (
                    <div key={k} style={caixa}>
                      <div style={{ fontWeight: 600, fontSize: 12.5, color: textMain, marginBottom: 4 }}>{humano(k)}</div>
                      <pre style={pre}>{v}</pre>
                    </div>
                  ))}
                  {blocos.map(([k, v]) => (
                    <div key={k} className="mb-2">
                      {blocos.length > 1 && <div style={{ fontSize: 12, color: textMuted, fontWeight: 700, margin: "4px 0" }}>{humano(k)}</div>}
                      {itensDe(v).map((it, i) => {
                        const st = situacaoTarefa(primeiro(it, CONV_STATUS));
                        const res = primeiro(it, ["resultado", "resposta", "saida", "conteudo", "texto", "valor", "extracao"]);
                        return (
                          <div key={i} style={caixa}>
                            <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                              <span style={{ fontWeight: 600, fontSize: 12.5, color: textMain }}>
                                {humano(primeiro(it, ["nome", "prompt_codigo", "titulo", "tipo", "prompt", "chave"]) || `item ${i + 1}`)}
                              </span>
                              {st.texto !== "—" && <Badge cor={st.cor} dica={st.dica}>{st.texto}</Badge>}
                            </div>
                            {res === undefined ? (
                              <span style={{ fontSize: 12, color: textMuted }}>sem resultado ainda</span>
                            ) : (
                              <pre style={pre}>{textoDoResultado(res)}</pre>
                            )}
                            {st.dica && /incert/i.test(st.texto) && <div style={{ fontSize: 11.5, color: "#d97706", marginTop: 4 }}>{st.dica}</div>}
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  <button type="button" className="btn btn-sm btn-link px-0" onClick={() => setVerJson(!verJson)}>
                    <i className={`ti ti-chevron-${verJson ? "up" : "down"} me-1`} />JSON completo da discadora
                  </button>
                  {verJson && (
                    <pre style={{ ...pre, ...caixa, fontFamily: "monospace", fontSize: 11.5, maxHeight: 300, overflow: "auto" }}>
                      {JSON.stringify(d, null, 2)}
                    </pre>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Discadora;
